import clsx from 'clsx';
import {
  AlertTriangle,
  Check,
  ChevronRight,
  CircleDashed,
  Info,
  Loader2,
  ShieldAlert,
  X,
  type LucideIcon,
} from 'lucide-react';
import {
  forwardRef,
  useEffect,
  useRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

/**
 * ODR UI kit.
 *
 * Rule that shapes everything here: status is never carried by colour alone
 * (directive §51/§63). Every Badge, StatusPill and answer control pairs its
 * colour with a word and, where it matters, an icon.
 */

// ---------------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------------

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';
type ButtonSize = 'sm' | 'md' | 'lg';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-steel-800 text-white hover:bg-steel-700 active:bg-steel-900 disabled:bg-steel-300',
  secondary:
    'bg-white text-steel-800 border border-steel-300 hover:bg-steel-50 active:bg-steel-100 disabled:text-steel-400',
  ghost: 'text-steel-700 hover:bg-steel-100 active:bg-steel-200 disabled:text-steel-400',
  danger: 'bg-alarm-600 text-white hover:bg-alarm-700 active:bg-alarm-700 disabled:bg-alarm-100',
  success: 'bg-ok-600 text-white hover:bg-ok-700 active:bg-ok-700 disabled:bg-ok-100',
};

const BUTTON_SIZES: Record<ButtonSize, string> = {
  sm: 'min-h-9 px-3 text-sm gap-1.5',
  md: 'min-h-11 px-4 text-sm gap-2',
  lg: 'min-h-touch px-5 text-base gap-2',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: LucideIcon;
  fullWidth?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading, icon: Icon, fullWidth, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={clsx(
        'inline-flex items-center justify-center rounded-lg font-semibold transition-colors',
        'disabled:cursor-not-allowed',
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? (
        <Loader2 aria-hidden className="size-4 animate-spin" />
      ) : Icon ? (
        <Icon aria-hidden className="size-4 shrink-0" />
      ) : null}
      {children}
    </button>
  );
});

// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------

export type Tone = 'ok' | 'warn' | 'alarm' | 'info' | 'neutral';

const TONE_CLASSES: Record<Tone, string> = {
  ok: 'bg-ok-50 text-ok-700 border-ok-500/40',
  warn: 'bg-warn-50 text-warn-700 border-warn-500/40',
  alarm: 'bg-alarm-50 text-alarm-700 border-alarm-500/40',
  info: 'bg-info-50 text-info-700 border-info-500/40',
  neutral: 'bg-steel-100 text-steel-700 border-steel-300',
};

const TONE_DOT: Record<Tone, string> = {
  ok: 'bg-ok-500',
  warn: 'bg-warn-500',
  alarm: 'bg-alarm-500',
  info: 'bg-info-500',
  neutral: 'bg-steel-400',
};

export function Badge({
  tone = 'neutral',
  children,
  icon: Icon,
  className,
}: {
  tone?: Tone;
  children: ReactNode;
  icon?: LucideIcon;
  className?: string;
}) {
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-semibold whitespace-nowrap',
        TONE_CLASSES[tone],
        className,
      )}
    >
      {Icon ? <Icon aria-hidden className="size-3.5" /> : null}
      {children}
    </span>
  );
}

/** Badge with a status dot. The label is always present — never colour alone. */
export function StatusPill({
  tone,
  label,
  className,
}: {
  tone: Tone;
  label: string;
  className?: string;
}) {
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-semibold whitespace-nowrap',
        TONE_CLASSES[tone],
        className,
      )}
    >
      <span aria-hidden className={clsx('size-2 rounded-full', TONE_DOT[tone])} />
      {label}
    </span>
  );
}

// --- Domain status → tone mappings (single source for the whole UI) --------

export function runStatusTone(status: string): Tone {
  switch (status) {
    case 'VERIFIED':
      return 'ok';
    case 'SUBMITTED':
    case 'COMPLETED':
      return 'info';
    case 'IN_PROGRESS':
    case 'PARTIALLY_COMPLETED':
      return 'warn';
    case 'REJECTED':
      return 'alarm';
    default:
      return 'neutral';
  }
}

export function problemStatusTone(status: string): Tone {
  switch (status) {
    case 'CLOSED':
      return 'ok';
    case 'OPEN':
    case 'ACKNOWLEDGED':
      return 'alarm';
    case 'ASSIGNED':
    case 'IN_PROGRESS':
      return 'warn';
    case 'PENDING_VERIFICATION':
      return 'info';
    default:
      return 'neutral';
  }
}

export function severityTone(severity: string): Tone {
  switch (severity) {
    case 'CRITICAL':
      return 'alarm';
    case 'HIGH':
      return 'warn';
    case 'MEDIUM':
      return 'info';
    default:
      return 'neutral';
  }
}

export function oplStatusTone(status: string): Tone {
  switch (status) {
    case 'PUBLISHED':
      return 'ok';
    case 'APPROVED':
      return 'info';
    case 'SUBMITTED':
    case 'IN_REVIEW':
      return 'warn';
    case 'ARCHIVED':
      return 'neutral';
    default:
      return 'neutral';
  }
}

export function complianceTone(status: string): Tone {
  switch (status) {
    case 'DONE':
      return 'ok';
    case 'IN_PROGRESS':
      return 'info';
    case 'DUE':
      return 'neutral';
    case 'OVERDUE':
      return 'warn';
    case 'MISSED':
      return 'alarm';
    default:
      return 'neutral';
  }
}

// ---------------------------------------------------------------------------
// Surfaces
// ---------------------------------------------------------------------------

export function Card({
  children,
  className,
  as: Component = 'div',
}: {
  children: ReactNode;
  className?: string;
  as?: 'div' | 'section' | 'article' | 'li';
}) {
  return (
    <Component
      className={clsx('rounded-xl border border-steel-200 bg-white shadow-sm', className)}
    >
      {children}
    </Component>
  );
}

export function CardHeader({
  title,
  subtitle,
  action,
  className,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={clsx(
        'flex items-start justify-between gap-3 border-b border-steel-200 px-4 py-3',
        className,
      )}
    >
      <div className="min-w-0">
        <h2 className="truncate text-sm font-bold text-steel-900">{title}</h2>
        {subtitle ? <p className="mt-0.5 text-xs text-steel-500">{subtitle}</p> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

export function Stat({
  label,
  value,
  hint,
  tone = 'neutral',
  icon: Icon,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: Tone;
  icon?: LucideIcon;
}) {
  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-semibold tracking-wide text-steel-500 uppercase">{label}</p>
        {Icon ? <Icon aria-hidden className="size-4 text-steel-400" /> : null}
      </div>
      <p
        className={clsx(
          'mt-2 text-3xl font-bold tabular-nums',
          tone === 'alarm' && 'text-alarm-600',
          tone === 'warn' && 'text-warn-700',
          tone === 'ok' && 'text-ok-700',
          tone === 'neutral' && 'text-steel-900',
          tone === 'info' && 'text-info-700',
        )}
      >
        {value}
      </p>
      {hint ? <p className="mt-1 text-xs text-steel-500">{hint}</p> : null}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Feedback
// ---------------------------------------------------------------------------

export function EmptyState({
  title,
  message,
  icon: Icon = CircleDashed,
  action,
}: {
  title: string;
  message: string;
  icon?: LucideIcon;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
      <Icon aria-hidden className="size-10 text-steel-300" />
      <h3 className="mt-3 text-sm font-bold text-steel-800">{title}</h3>
      <p className="mt-1 max-w-sm text-sm text-steel-500">{message}</p>
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div role="status" className="flex items-center justify-center gap-2 py-10 text-steel-500">
      <Loader2 aria-hidden className="size-5 animate-spin" />
      <span className="text-sm">{label}…</span>
    </div>
  );
}

export function SkeletonRows({ rows = 4 }: { rows?: number }) {
  return (
    <div className="space-y-2 p-4" aria-hidden>
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="h-12 animate-pulse rounded-lg bg-steel-100" />
      ))}
    </div>
  );
}

const CALLOUT_ICON: Record<Tone, LucideIcon> = {
  ok: Check,
  warn: AlertTriangle,
  alarm: ShieldAlert,
  info: Info,
  neutral: Info,
};

export function Callout({
  tone = 'info',
  title,
  children,
  className,
}: {
  tone?: Tone;
  title?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  const Icon = CALLOUT_ICON[tone];
  return (
    <div
      role={tone === 'alarm' ? 'alert' : undefined}
      className={clsx('flex gap-3 rounded-lg border p-3', TONE_CLASSES[tone], className)}
    >
      <Icon aria-hidden className="mt-0.5 size-4 shrink-0" />
      <div className="min-w-0 text-sm">
        {title ? <p className="font-bold">{title}</p> : null}
        {children ? <div className={clsx(title && 'mt-0.5')}>{children}</div> : null}
      </div>
    </div>
  );
}

/** Safety and quality warnings — deliberately loud (directive §52). */
export function SafetyWarning({ children }: { children: ReactNode }) {
  return (
    <div
      role="alert"
      className="flex gap-2 rounded-lg border-2 border-alarm-500 bg-alarm-50 p-3 text-sm text-alarm-700"
    >
      <ShieldAlert aria-hidden className="mt-0.5 size-5 shrink-0" />
      <div>
        <p className="text-xs font-extrabold tracking-wider uppercase">Safety</p>
        <p className="mt-0.5 font-medium">{children}</p>
      </div>
    </div>
  );
}

export function QualityWarning({ children }: { children: ReactNode }) {
  return (
    <div className="flex gap-2 rounded-lg border-2 border-warn-500 bg-warn-50 p-3 text-sm text-warn-700">
      <AlertTriangle aria-hidden className="mt-0.5 size-5 shrink-0" />
      <div>
        <p className="text-xs font-extrabold tracking-wider uppercase">Quality</p>
        <p className="mt-0.5 font-medium">{children}</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Forms
// ---------------------------------------------------------------------------

export function Field({
  label,
  hint,
  error,
  required,
  children,
  className,
}: {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={clsx('block', className)}>
      <span className="mb-1 flex items-center gap-1 text-sm font-semibold text-steel-800">
        {label}
        {required ? (
          <span className="text-alarm-600" aria-label="required">
            *
          </span>
        ) : null}
      </span>
      {children}
      {hint && !error ? <span className="mt-1 block text-xs text-steel-500">{hint}</span> : null}
      {error ? (
        <span role="alert" className="mt-1 block text-xs font-semibold text-alarm-600">
          {error}
        </span>
      ) : null}
    </label>
  );
}

const CONTROL =
  'w-full rounded-lg border border-steel-300 bg-white px-3 py-2.5 text-steel-900 ' +
  'placeholder:text-steel-400 focus:border-info-500 focus:ring-2 focus:ring-info-500/20 ' +
  'disabled:bg-steel-50 disabled:text-steel-500';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    return <input ref={ref} className={clsx(CONTROL, 'min-h-11', className)} {...rest} />;
  },
);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, rows = 3, ...rest }, ref) {
    return <textarea ref={ref} rows={rows} className={clsx(CONTROL, className)} {...rest} />;
  },
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, children, ...rest }, ref) {
    return (
      <select ref={ref} className={clsx(CONTROL, 'min-h-11', className)} {...rest}>
        {children}
      </select>
    );
  },
);

// ---------------------------------------------------------------------------
// Overlays
// ---------------------------------------------------------------------------

/**
 * Right-side detail panel (directive §50).
 * On phones it becomes a full-height sheet; on desktop a fixed-width drawer.
 */
export function Drawer({
  open,
  onClose,
  title,
  subtitle,
  footer,
  children,
  width = 'md',
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  width?: 'md' | 'lg';
}) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    // Move focus into the panel so keyboard users are not stranded behind it.
    panelRef.current?.focus();
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        type="button"
        aria-label="Close panel"
        className="absolute inset-0 bg-steel-950/40"
        onClick={onClose}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : 'Details'}
        tabIndex={-1}
        className={clsx(
          'relative flex h-full w-full flex-col bg-white shadow-2xl outline-none',
          width === 'lg' ? 'sm:max-w-2xl' : 'sm:max-w-lg',
        )}
      >
        <div className="flex items-start justify-between gap-3 border-b border-steel-200 px-4 py-3 safe-top">
          <div className="min-w-0">
            <h2 className="truncate text-base font-bold text-steel-900">{title}</h2>
            {subtitle ? <p className="mt-0.5 text-xs text-steel-500">{subtitle}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-2 text-steel-500 hover:bg-steel-100"
          >
            <X aria-hidden className="size-5" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">{children}</div>
        {footer ? (
          <div className="border-t border-steel-200 bg-steel-50 px-4 py-3 safe-bottom">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <button
        type="button"
        aria-label="Close dialog"
        className="absolute inset-0 bg-steel-950/40"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        className="relative flex max-h-[90vh] w-full flex-col rounded-t-2xl bg-white shadow-2xl sm:max-w-lg sm:rounded-2xl"
      >
        <div className="flex items-center justify-between border-b border-steel-200 px-4 py-3">
          <h2 className="text-base font-bold text-steel-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-2 text-steel-500 hover:bg-steel-100"
          >
            <X aria-hidden className="size-5" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">{children}</div>
        {footer ? (
          <div className="border-t border-steel-200 px-4 py-3 safe-bottom">{footer}</div>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Misc
// ---------------------------------------------------------------------------

export function ProgressBar({
  value,
  total,
  tone = 'info',
  label,
}: {
  value: number;
  total: number;
  tone?: Tone;
  label?: string;
}) {
  const percent = total === 0 ? 0 : Math.round((value / total) * 100);
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between text-xs font-semibold text-steel-600">
        <span>{label ?? 'Progress'}</span>
        <span className="tabular-nums">
          {value} / {total}
        </span>
      </div>
      <div
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-label={label ?? 'Progress'}
        className="h-2.5 w-full overflow-hidden rounded-full bg-steel-200"
      >
        <div
          className={clsx('h-full rounded-full transition-all', TONE_DOT[tone])}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}

export function ListRow({
  onClick,
  children,
  className,
}: {
  onClick?: () => void;
  children: ReactNode;
  className?: string;
}) {
  const content = (
    <div
      className={clsx(
        'flex w-full items-center gap-3 px-4 py-3 text-left',
        onClick && 'hover:bg-steel-50 active:bg-steel-100',
        className,
      )}
    >
      <div className="min-w-0 flex-1">{children}</div>
      {onClick ? <ChevronRight aria-hidden className="size-4 shrink-0 text-steel-400" /> : null}
    </div>
  );

  return onClick ? (
    <button type="button" onClick={onClick} className="block w-full">
      {content}
    </button>
  ) : (
    content
  );
}

export function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="border-b border-steel-100 py-2.5 last:border-0">
      <dt className="text-xs font-semibold tracking-wide text-steel-500 uppercase">{label}</dt>
      <dd className="mt-1 text-sm text-steel-900">{children ?? '—'}</dd>
    </div>
  );
}
